import React, { useEffect, useId, useRef } from 'react'
import { ReactComponent as CheckIcon } from '@assets/images/Icons/Checkbox_check.svg'
import { ReactComponent as IndeterminateIcon } from '@assets/images/Icons/Checkbox_indeterminate.svg'
import type { CheckboxProps } from './Checkbox.types'
import './Checkbox.css'

const Checkbox: React.FC<CheckboxProps> = ({
  label,
  checked,
  defaultChecked,
  indeterminate = false,
  disabled,
  darkMode = false,
  previewState,
  className = '',
  id,
  onChange,
  ...rest
}) => {
  const generatedId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const inputId = id ?? generatedId
  const isPreview = previewState != null
  const isDisabled = disabled || previewState === 'disabled'
  const isChecked = Boolean(checked ?? defaultChecked)

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate
    }
  }, [indeterminate])

  const classNames = [
    'ds-checkbox',
    isChecked && !indeterminate ? 'ds-checkbox--checked' : '',
    indeterminate ? 'ds-checkbox--indeterminate' : '',
    isDisabled ? 'ds-checkbox--disabled' : '',
    darkMode ? 'ds-checkbox--dark' : '',
    isPreview ? 'ds-checkbox--preview' : '',
    previewState ? `ds-checkbox--preview-${previewState}` : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const inputProps =
    checked !== undefined ? { checked, onChange } : { defaultChecked, onChange }

  return (
    <label className={classNames} htmlFor={inputId}>
      <input
        ref={inputRef}
        id={inputId}
        type="checkbox"
        className="ds-checkbox__input"
        disabled={isDisabled}
        data-preview-state={previewState}
        {...inputProps}
        {...rest}
      />
      <span className="ds-checkbox__control" aria-hidden="true">
        {indeterminate ? (
          <IndeterminateIcon className="ds-checkbox__icon" />
        ) : isChecked ? (
          <CheckIcon className="ds-checkbox__icon" />
        ) : null}
      </span>
      {label != null && label !== '' ? (
        <span className="ds-checkbox__label">{label}</span>
      ) : null}
    </label>
  )
}

export default Checkbox
